// @ts-check

/**
 * @module files/validation
 */

export const fileErrors = {
    unknown: 'unknown',
    tooLarge: 'tooLarge',
    tooSmall: 'tooSmall',
    nameTooLong: 'nameTooLong',
    invalidFileName: 'invalidFileName',
    invalidExtension: 'invalidExtension',
};

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - If the result is valid
 * @property {string} error - Error type if the result is invalid
 */

/**
 * @param {File} file - File to validate
 * @param {object} validationSettings Settings for file validation
 * @param {string[]} validationSettings.invalidExtensions
 * @param {string[] | null} validationSettings.allowedExtensions
 * @param {string[]} validationSettings.invalidFileNameCharacters
 * @param {number} validationSettings.maxFileNameLength
 * @param {number} validationSettings.maxFileSizeBytes
 * @returns {ValidationResult}
 */
export function validateFile(file, { invalidExtensions, allowedExtensions, invalidFileNameCharacters, maxFileNameLength, maxFileSizeBytes }) {
    if (file.size > maxFileSizeBytes) {
        return { valid: false, error: fileErrors.tooLarge };
    }

    if (file.size <= 0) {
        return { valid: false, error: fileErrors.tooSmall };
    }

    return validateFileName(file.name, invalidExtensions, invalidFileNameCharacters, maxFileNameLength, allowedExtensions);
}

/**
 * @param {string} fileName Name of file to validate
 * @param {string[]} invalidExtensions
 * @param {string[]} invalidFileNameCharacters
 * @param {number} maxFileNameLength
 * @param {string[] | null} allowedExtensions
 * @returns {ValidationResult}
 */
export function validateFileName(fileName, invalidExtensions, invalidFileNameCharacters, maxFileNameLength, allowedExtensions = null) {
    if (fileName.length > maxFileNameLength) {
        return { valid: false, error: fileErrors.nameTooLong };
    }

    if (fileName.trim() === '') {
        return { valid: false, error: fileErrors.invalidFileName };
    }

    for (const ch of invalidFileNameCharacters) {
        if (fileName.indexOf(ch) !== -1) {
            return { valid: false, error: fileErrors.invalidFileName };
        }
    }

    const extension = fileName.split('.').pop().toLowerCase();

    if (invalidExtensions.indexOf(extension) !== -1) {
        return { valid: false, error: fileErrors.invalidExtension };
    }

    if (allowedExtensions && allowedExtensions.length > 0 && allowedExtensions.indexOf(extension) < 0) {
        return { valid: false, error: fileErrors.invalidExtension };
    }

    return { valid: true, error: undefined };
}
